/**
 * Thin WebGL2 helpers. This is the seed of the RenderBackend abstraction (ADR-0002): visualizations
 * only ever see programs, uniforms and textures through these helpers.
 */

export type GL = WebGL2RenderingContext;

export const FULLSCREEN_VS = `#version 300 es
out vec2 vUv;
void main() {
  vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
  vUv = p;
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}`;

export class ShaderError extends Error {}

export interface Program {
  program: WebGLProgram;
  uniforms: Map<string, WebGLUniformLocation | null>;
}

function compile(gl: GL, type: number, src: string): WebGLShader {
  const sh = gl.createShader(type);
  if (!sh) throw new ShaderError('createShader failed');
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  return sh;
}

/** Start compiling. With KHR_parallel_shader_compile this returns before compilation finishes. */
export function createProgram(gl: GL, vs: string, fs: string): WebGLProgram {
  const program = gl.createProgram();
  if (!program) throw new ShaderError('createProgram failed');
  const v = compile(gl, gl.VERTEX_SHADER, vs);
  const f = compile(gl, gl.FRAGMENT_SHADER, fs);
  gl.attachShader(program, v);
  gl.attachShader(program, f);
  gl.linkProgram(program);
  gl.deleteShader(v);
  gl.deleteShader(f);
  return program;
}

/** True once linking has finished (always true without the parallel-compile extension). */
export function isProgramReady(
  gl: GL,
  program: WebGLProgram,
  parallel: { COMPLETION_STATUS_KHR: number } | null,
): boolean {
  if (!parallel) return true;
  return gl.getProgramParameter(program, parallel.COMPLETION_STATUS_KHR) === true;
}

/** Check link status (call only once ready; this blocks if compilation is still running). */
export function finishProgram(gl: GL, program: WebGLProgram): Program {
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program) ?? 'unknown link error';
    gl.deleteProgram(program);
    throw new ShaderError(log);
  }
  return { program, uniforms: new Map() };
}

export function uniform(gl: GL, p: Program, name: string): WebGLUniformLocation | null {
  let loc = p.uniforms.get(name);
  if (loc === undefined) {
    loc = gl.getUniformLocation(p.program, name);
    p.uniforms.set(name, loc);
  }
  return loc;
}

/** A width×height (default ×1) texture with immutable storage; fill it with texSubImage2D. */
export function createDataTexture(
  gl: GL,
  width: number,
  internalFormat: number,
  height = 1,
): WebGLTexture {
  const tex = gl.createTexture();
  if (!tex) throw new Error('createTexture failed');
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texStorage2D(gl.TEXTURE_2D, 1, internalFormat, width, height);
  return tex;
}

export type TargetFormat = 'rgba8' | 'rgba16f';

export interface RenderTarget {
  fbo: WebGLFramebuffer;
  texture: WebGLTexture;
  width: number;
  height: number;
  format: TargetFormat;
}

/** Texture unit reserved for allocations, so creating textures never disturbs bound inputs. */
const SCRATCH_UNIT = 15;

export function createRenderTarget(
  gl: GL,
  width: number,
  height: number,
  format: TargetFormat = 'rgba8',
): RenderTarget {
  const texture = gl.createTexture();
  const fbo = gl.createFramebuffer();
  if (!texture || !fbo) throw new Error('render target allocation failed');
  const prevUnit = gl.getParameter(gl.ACTIVE_TEXTURE) as number;
  gl.activeTexture(gl.TEXTURE0 + SCRATCH_UNIT);
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texStorage2D(gl.TEXTURE_2D, 1, format === 'rgba16f' ? gl.RGBA16F : gl.RGBA8, width, height);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
  gl.clearColor(0, 0, 0, 1);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  // Don't leave it bound anywhere (a bound texture that is also a draw target is a feedback loop).
  gl.bindTexture(gl.TEXTURE_2D, null);
  gl.activeTexture(prevUnit);
  return { fbo, texture, width, height, format };
}

export function deleteRenderTarget(gl: GL, rt: RenderTarget): void {
  gl.deleteFramebuffer(rt.fbo);
  gl.deleteTexture(rt.texture);
}

/**
 * Pool of render targets keyed by size and format. Temporaries are acquired and released every
 * frame; targets unused for a while are freed (docs/04 "Resource management").
 */
export class TargetPool {
  private free: { rt: RenderTarget; lastUsed: number }[] = [];
  private frame = 0;
  private outstanding = 0;

  constructor(private readonly gl: GL) {}

  acquire(width: number, height: number, format: TargetFormat = 'rgba8'): RenderTarget {
    this.outstanding++;
    const i = this.free.findIndex(
      (f) => f.rt.width === width && f.rt.height === height && f.rt.format === format,
    );
    if (i >= 0) return this.free.splice(i, 1)[0]!.rt;
    return createRenderTarget(this.gl, width, height, format);
  }

  release(rt: RenderTarget | null | undefined): void {
    if (!rt) return;
    this.outstanding--;
    this.free.push({ rt, lastUsed: this.frame });
  }

  /** Call once per frame: frees targets idle for more than ~2 seconds. */
  endFrame(): void {
    this.frame++;
    const keep: typeof this.free = [];
    for (const f of this.free) {
      if (this.frame - f.lastUsed > 120) deleteRenderTarget(this.gl, f.rt);
      else keep.push(f);
    }
    this.free = keep;
  }

  /** Approximate GPU memory held (bytes). */
  get bytes(): number {
    return this.free.reduce(
      (s, f) => s + f.rt.width * f.rt.height * (f.rt.format === 'rgba16f' ? 8 : 4),
      0,
    );
  }

  get inUse(): number {
    return this.outstanding;
  }

  clear(): void {
    for (const f of this.free) deleteRenderTarget(this.gl, f.rt);
    this.free = [];
  }
}

/** Bind a texture to a unit and point a sampler uniform at it. */
export function bindTex(gl: GL, p: Program, name: string, unit: number, tex: WebGLTexture): void {
  gl.activeTexture(gl.TEXTURE0 + unit);
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.uniform1i(uniform(gl, p, name), unit);
}

/** Unbind texture units used as pass inputs (avoids feedback-loop errors on the next draw). */
export function unbindUnits(gl: GL, from: number, to: number): void {
  for (let u = from; u <= to; u++) {
    gl.activeTexture(gl.TEXTURE0 + u);
    gl.bindTexture(gl.TEXTURE_2D, null);
  }
}

/** Compile and link synchronously (for small built-in passes). */
export function buildProgram(gl: GL, vs: string, fs: string): Program {
  return finishProgram(gl, createProgram(gl, vs, fs));
}
